import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider } from './context/AppContext';
import { AdminAuthProvider, useAdminAuth } from './admin/AdminAuthContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ToastContainer from './components/ui/Toast';
import AdminLayout from './admin/AdminLayout';

// Public pages
const Home = lazy(() => import('./pages/Home'));
const Article = lazy(() => import('./pages/Article'));
const Category = lazy(() => import('./pages/Category'));
const About = lazy(() => import('./pages/About'));
const Articles = lazy(() => import('./pages/Articles'));

// Admin pages
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminArticles = lazy(() => import('./admin/pages/AdminArticles'));
const AdminArticleEditor = lazy(() => import('./admin/pages/AdminArticleEditor'));
const AdminUsers = lazy(() => import('./admin/pages/AdminUsers'));
const AdminCategories = lazy(() => import('./admin/pages/AdminCategories'));
const AdminMedia = lazy(() => import('./admin/pages/AdminMedia'));
const AdminMenu    = lazy(() => import('./admin/pages/AdminMenu'));
const AdminWidgets    = lazy(() => import('./admin/pages/AdminWidgets'));
const AdminDiscussion  = lazy(() => import('./admin/pages/AdminDiscussion'));
const AdminBlockStyles = lazy(() => import('./admin/pages/AdminBlockStyles'));
const AdminFooter      = lazy(() => import('./admin/pages/AdminFooter'));

const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div className="spinner" />
  </div>
);


const NotFound = () => (
  <div style={{ textAlign: 'center', padding: '100px 24px', minHeight: '60vh' }}>
    <span style={{ fontFamily: 'var(--font-display)', fontSize: '8rem', fontWeight: 900, color: 'var(--ink-soft)', display: 'block' }}>404</span>
    <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--text-primary)', marginBottom: 12 }}>Page Not Found</h1>
    <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>The page you're looking for doesn't exist.</p>
    <a href="/" className="btn btn-primary">← Return Home</a>
  </div>
);

const ScrollToTop = () => {
  const { useEffect } = React;
  const { useLocation } = require('react-router-dom');
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return null;
};

// Protected admin route
function AdminRoute({ children }) {
  const { user, loading } = useAdminAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function App() {
  return (
    <HelmetProvider>
    <AppProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/articles" element={<AdminRoute><AdminArticles /></AdminRoute>} />
              <Route path="/admin/articles/new" element={<AdminRoute><AdminArticleEditor /></AdminRoute>} />
              <Route path="/admin/articles/:id/edit" element={<AdminRoute><AdminArticleEditor /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/users/new" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
              <Route path="/admin/menu"    element={<AdminRoute><AdminMenu /></AdminRoute>} />
              <Route path="/admin/widgets"    element={<AdminRoute><AdminWidgets /></AdminRoute>} />
              <Route path="/admin/discussion"   element={<AdminRoute><AdminDiscussion /></AdminRoute>} />
              <Route path="/admin/block-styles" element={<AdminRoute><AdminBlockStyles /></AdminRoute>} />
              <Route path="/admin/footer"       element={<AdminRoute><AdminFooter /></AdminRoute>} />
              <Route path="/admin/media" element={<AdminRoute><AdminMedia /></AdminRoute>} />

              {/* Public routes */}
              <Route path="/" element={<><Header /><main><Home /></main><Footer /></>} />
              <Route path="/article/:slug" element={<><Header /><main><Article /></main><Footer /></>} />
              <Route path="/category/:slug" element={<><Header /><main><Category /></main><Footer /></>} />
              <Route path="/about" element={<><Header /><main><About /></main><Footer /></>} />
              <Route path="/articles" element={<><Header /><main><Articles /></main><Footer /></>} />
              <Route path="*" element={<><Header /><main><NotFound /></main><Footer /></>} />
            </Routes>
          </Suspense>
          <ToastContainer />
        </BrowserRouter>
      </AdminAuthProvider>
    </AppProvider>
    </HelmetProvider>
  );
}

export default App;
